import { ApiError, ERR_MSG_TIMEOUT, normalizeApiError } from '@/utils/AxiosUtilV2'
import { AlertKind, AsyncState, FetchAsyncStateOptions } from './interfaces/AsyncState'
import { cloneInitData, ensureMeta } from './internal/registry'

/**
 * Executa uma chamada assíncrona e escreve o resultado no estado.
 *
 * Assinatura equivalente à do `fetchReactiveResponseDTO`: os três primeiros
 * parâmetros são os mesmos e `customErrorMsg` continua aceito como quarto
 * posicional, ou dentro do objeto de opções.
 *
 * O que muda:
 *
 * 1. **Nenhuma resposta obsoleta escreve no estado.** Cada chamada recebe um
 *    token de sequência; se outra chamada começou nesse meio-tempo, o
 *    resultado é descartado. Sem isso, duas trocas rápidas de filtro fazem a
 *    resposta antiga sobrescrever a nova — inclusive `completed` e
 *    `successfully`. Como `ApiService` não aceita `signal`, descartar é a
 *    única defesa disponível.
 *
 * 2. **`successfully` é escrito pelo fluxo de controle**, não inferido da
 *    ausência de `errorMsg`.
 *
 * 3. **O erro guardado é um `ApiError`**, não o objeto plano do V1. Sendo
 *    subclasse de `Error`, o Vue não o transforma em proxy reativo.
 *
 * 4. **A escada de normalização saiu.** Ela existia para adivinhar o formato
 *    do erro; com `ApiError` garantido pelo wrapper V2, `axios` deixa de ser
 *    importado aqui.
 *
 * Função pura quanto a escopo: o alerta chega por `onAlert`, injetado pelo
 * `useAsyncState()`. Sem ele, `alert` é ignorado.
 */
export async function fetchAsyncState<T>(
  state: AsyncState<T>,
  callback: () => Promise<T>,
  alert: AlertKind | null = null,
  customErrorMsg: string | null | FetchAsyncStateOptions = null,
): Promise<void> {
  const options: FetchAsyncStateOptions =
    customErrorMsg !== null && typeof customErrorMsg === 'object'
      ? customErrorMsg
      : { customErrorMsg }

  const meta = ensureMeta(state)
  meta.seq += 1

  const token = meta.seq
  const isStale = () => meta.seq !== token

  let timeoutId: ReturnType<typeof setTimeout> | undefined

  // Reset síncrono, antes de qualquer await.
  if (options.resetData && meta.hasInitData) {
    state.data = cloneInitData(meta.initData as T)
  }

  state.loading = true
  state.error = null
  state.errorMsg = null
  state.successfully = false
  state.completed = false

  try {
    const pending = callback()

    const result = options.timeout && options.timeout > 0
      ? await Promise.race([pending, rejectAfter<T>(options.timeout, (id) => { timeoutId = id })])
      : await pending

    if (isStale()) return

    state.data = result
    state.successfully = true
  } catch (error) {
    if (isStale()) return

    const normalized = normalizeApiError(error)

    state.error = normalized
    state.errorMsg =
      typeof options.customErrorMsg === 'string' && options.customErrorMsg.length > 0
        ? options.customErrorMsg
        : normalized.message

    if (alert && options.onAlert) {
      options.onAlert(state.errorMsg, alert)
    }
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId)

    if (!isStale()) {
      state.loading = false
      state.completed = true
    }
  }
}

/**
 * Timeout no nível da promise.
 *
 * A requisição HTTP continua rodando — `ApiService` não expõe
 * `AxiosRequestConfig`, então não há `signal` nem `timeout` de rede. O ganho é
 * o estado não ficar preso em `loading: true` indefinidamente.
 */
function rejectAfter<T>(ms: number, onSchedule: (id: ReturnType<typeof setTimeout>) => void): Promise<T> {
  return new Promise<T>((_, reject) => {
    const id = setTimeout(
      () => reject(new ApiError({ message: ERR_MSG_TIMEOUT, code: 'TIMEOUT' })),
      ms,
    )

    onSchedule(id)
  })
}
